import React, { useState, useEffect } from "react";
import { PlayCircle, Lock, Bookmark } from "lucide-react";
import SurveyForMeSurveyCard from "./SurveyForMeSurveyCard";
import londonSurveyImage from "../../assets/img/Rectangle23.jpg";
import { resolvePhotoUrl, surveyPhoto } from "../../utils/profilePhoto";
import { isSurveyExpired } from "../../utils/surveyPresentation";
import { getSurveyForMe } from "../../service/surveyForMe";
import bookicon from "../../assets/img/bookicon.webp";

// Normalization function to map API survey response data to components' expected structure
const mapSurveyData = (apiSurvey) => {

  // console.log(
  //   "Survey ID:",
  //   apiSurvey.id,
  //   "is_saved:",
  //   apiSurvey.is_saved
  // );

  let tagsList = [];
  const rawTags = apiSurvey.survey_tags || apiSurvey.tags;
  if (Array.isArray(rawTags)) {
    tagsList = rawTags;
  } else if (typeof rawTags === "string" && rawTags.trim()) {
    tagsList = rawTags.split(",").map((t) => t.trim()).filter(Boolean);
  } else {
    tagsList = [apiSurvey.sector, apiSurvey.industry, apiSurvey.country].filter(Boolean);
  }

  const formattedTags = tagsList.map((tag) => {
    const clean = String(tag).trim();
    return clean.startsWith("#") ? clean : `#${clean}`;
  });

  const isExpired = isSurveyExpired(apiSurvey.end_date);

  return {
    id: apiSurvey.id,
    savedId: apiSurvey.saved_id,
    is_saved: apiSurvey.is_saved,   // <-- Ye line add karo
    creator_name: apiSurvey.creator_name,
    job_title: apiSurvey.job_title,
    start_date: apiSurvey.start_date,
    author: {
      name: apiSurvey.creator_name || "Survey creator",
      jobTitle: apiSurvey.job_title || "Product Researcher",
      avatar: surveyPhoto(apiSurvey),
    },
    postedAt: apiSurvey.survey_created_on || apiSurvey.created_on,
    coverImage: resolvePhotoUrl(apiSurvey.survey_image, londonSurveyImage),
    title: apiSurvey.survey_title || "Untitled Survey",
    description: apiSurvey.survey_description || "No description provided.",
    rewardCoins: parseFloat(apiSurvey.reward_per_participant) || 0,
    tags: formattedTags,
    participation_status: apiSurvey.participation_status,
    endDate: apiSurvey.end_date,
    isExpired: isExpired,
    like_count: apiSurvey.like_count,
    comment_count: apiSurvey.comment_count,
    liked_by_me: apiSurvey.liked_by_me,
    is_liked: apiSurvey.is_liked,
    likes_count: apiSurvey.likes_count,
    comments_count: apiSurvey.comments_count,
  };
};

const SurveyForMe = () => {
  const [activeTab, setActiveTab] = useState("available"); // 'available' | 'completed' | 'saved'
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ available: 0, completed: 0, saved: 0 });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);


  const visibleSurveys = activeTab === "available"
    ? surveys.filter((survey) => !isSurveyExpired(survey.endDate, new Date(now)))
    : surveys;
  const currentTabCount = activeTab === "available" ? visibleSurveys.length : counts[activeTab] || 0;


  useEffect(() => {
  
    const fetchSurveys = async () => {
      try {
        setSurveys([]);
        setLoading(true);

        const [availableRes, completedRes, savedRes] = await Promise.all([
          getSurveyForMe("available").catch(() => null),
          getSurveyForMe("completed").catch(() => null),
          getSurveyForMe("saved").catch(() => null)
        ]);

        const availableData = (availableRes?.data?.data || []).filter((item) => !isSurveyExpired(item.end_date));
        const completedData = completedRes?.data?.data || [];
        const savedData = savedRes?.data?.data || [];
        const completedIds = new Set(completedData.map((item) => String(item.id)));

        let apiData = [];
        if (activeTab === "available") {
          apiData = availableData;
        } else if (activeTab === "completed") {
          apiData = completedData;
        } else if (activeTab === "saved") {
          apiData = savedData;
        }

        const mapped = apiData.map((item) => mapSurveyData({
          ...item,
          participation_status: completedIds.has(String(item.id)) ? 1 : item.participation_status,
        }));
        setSurveys(mapped);

        setCounts({
          available: availableData.length,
          completed: completedRes?.data?.count || 0,
          saved: savedRes?.data?.count || 0
        });

      } catch (err) {
        console.error(`Error fetching ${activeTab} surveys:`, err);
        setSurveys([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();

   
  }, [activeTab]);

  const totalRewards = visibleSurveys.reduce((sum, survey) => sum + survey.rewardCoins, 0);

  const tabItems = [
    { id: "available", label: "Available", icon: PlayCircle },
    { id: "completed", label: "Completed", icon: Lock },
    { id: "saved", label: "Saved Surveys", icon: Bookmark },
  ];

  return (
    <div className="dashboard-create-survey surveys-for-me-page">
      {/* Top Header Card */}
      <div
        className="bg-white border mb-3"
        style={{ borderColor: "#ede7e1", borderRadius: "16px", padding: "20px 24px" }}
      >
        <h1 className="fw-bold m-0 mb-1" style={{ fontSize: "24px", color: "#111827" }}>
          Surveys For Me
        </h1>
        <p className="m-0" style={{ fontSize: "14px", color: "#4B5563" }}>
          {loading ? (
            "Loading surveys..."
          ) : (
            <>
              {/* {surveys.length} {surveys.length === 1 ? "Survey" : "Surveys"}{" "} */}

              {currentTabCount} {currentTabCount === 1 ? "Survey" : "Surveys"} {" "}

              
              {activeTab === "available" ? "Available" : activeTab === "completed" ? "Completed" : "Saved"}


              {activeTab === "available" && (
                <>
                  {" • "}
                  <span style={{ color: "#FF8A00", fontWeight: 700 }}>
                    Earn {totalRewards} Coins Across Available Surveys
                  </span>
                </>
              )}
            </>
          )}
        </p>
      </div>

      {/* Outer 3-Tab Card */}
      <div
        className="bg-white border mb-4"
        style={{ borderColor: "#ede7e1", borderRadius: "16px", padding: "16px 20px" }}
      >
        {/* Inner Tab Bar Container */}
        <div
          className="bg-white border position-relative d-flex align-items-center"
          style={{ borderColor: "#E5E7EB", borderRadius: "12px", padding: "12px 16px", overflow: "hidden" }}
        >



          {tabItems.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            // const tabCount = counts[tab.id] || (isActive ? surveys.length : tab.id === "available" ? 2 : tab.id === "completed" ? 1 : 2);
            const tabCount = tab.id === "available" ? (activeTab === "available" ? visibleSurveys.length : counts.available) : counts[tab.id] ?? 0;

            // const formattedCount = tab.id === "available" ? `(${String(tabCount).padStart(2, "0")})` : String(tabCount).padStart(2, "0");

            const formattedCount = `(${String(tabCount).padStart(2, "0")})`;

            console.log("Tab:", tab.id);
            console.log("Count State:", counts);
            console.log("Tab Count:", counts[tab.id]);

            return (
              <div
                key={tab.id}
                className="flex-fill text-center position-relative"
                style={{ flex: 1 }}
              >
                <button
                  type="button"
                  className="btn border-0 p-0 d-inline-flex align-items-center justify-content-center gap-2"
                  style={{
                    color: isActive ? "#C72430" : "#2B2D2F",
                    fontWeight: isActive ? "600" : "500",
                    fontSize: "15px",
                    cursor: "pointer",
                    background: "none",
                    paddingBottom: "8px",
                    paddingTop: "6px",
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={18} style={{ color: isActive ? "#C72430" : "#2B2D2F" }} />
                  <span>{tab.label}</span>
                  <span
                    className="ms-1"
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      backgroundColor: isActive ? "#FEE2E2" : "#E5E7EB",
                      color: isActive ? "#C72430" : "#2B2D2F",
                      borderRadius: "6px",
                      padding: "2px 8px",
                    }}
                  >
                    {formattedCount}
                  </span>
                </button>

                {/* Active Red Underline Indicator at bottom edge of inner container */}
                {isActive && (
                  <div
                    className="position-absolute bottom-0 start-0 w-100"
                    style={{
                      height: "3px",
                      backgroundColor: "#C72430",
                      borderRadius: "3px 3px 0 0",
                      marginBottom: "-12px",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Completed Banner */}
      {activeTab === "completed" && !loading && visibleSurveys.length > 0 && (
        <div className="sfm-completed-banner">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>Great Job! You Have Completed This Surveys. Share Your Feedback With Others.</span>
        </div>
      )}

      {/* Survey Cards List */}
      <div className="sfm-list">
        {loading ? (
          <div className="sfm-loading">
            <div className="spinner"></div>
            <p>Loading surveys...</p>
          </div>
        ) : visibleSurveys.length > 0 ? (
          visibleSurveys.map((survey) => (
            // <h1>12</h1>
            // <SurveyForMeSurveyCard
            //   key={survey.id}
            //   survey={survey}
            //   activeTab={activeTab}
            //   onRemoveSaved={(removedId) => setSurveys((prev) => prev.filter((s) => s.id !== removedId))}
            // />

            <SurveyForMeSurveyCard
              key={survey.id}
              survey={survey}
              activeTab={activeTab}
              now={now}
              onRemoveSaved={(removedId) =>
                setSurveys((prev) => prev.filter((s) => s.id !== removedId))
              }
              onToggleSaved={(id, isSaved) => {
                setSurveys((prev) =>
                  prev.map((item) =>
                    item.id === id
                      ? { ...item, is_saved: isSaved ? 1 : 0 }
                      : item
                  )
                );
              }}
            />
          ))
        ) : (
          <div className="sfm-empty-state">
            <div className="sfm-empty-icon  d-flex justify-content-center"><img src={bookicon} alt="bookicon"  className="img-fluid empty-icon-img" /></div>
            <h3>No {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Surveys Found</h3>
            <p>We couldn't find any {activeTab} surveys matching your profile at the moment.</p>
          </div>
        )}

        
      </div>
    </div>
  );
};

export default SurveyForMe;
